import { axiosInstance } from "@/helper/axios";
import api from "@/api/restaurants";
import {
  mergeCartStockSnapshots,
  normalizeCartStockSnapshot,
  reconcileCartNeedAllocations,
} from "@/helper/warehouseNeedCart";
import { normalizeSupplierOrderCartItem } from "@/helper/supplierOrderCart";
import store from "./index";
import { getEcho } from "@/helper/echo";

// LocalStorage key for cart persistence
const CART_STORAGE_KEY = "anyware_shopping_cart";

// Debounce timer for sync
let syncTimer = null;
const SYNC_DELAY = 300; // 300ms delay for debouncing

const normalizeCartItem = (item = {}) => {
  const supplierExternalProductId =
    item.supplierExternalProductId ??
    item.supplier?.supplier_external_product_id ??
    null;

  const supplier =
    item.supplier && typeof item.supplier === "object"
      ? {
          ...item.supplier,
          supplier_external_product_id:
            item.supplier.supplier_external_product_id ??
            supplierExternalProductId,
        }
      : item.supplier;

  const normalized = normalizeSupplierOrderCartItem({
    ...item,
    supplierExternalProductId,
    supplier,
  });

  if (item.stockSnapshot) {
    normalized.stockSnapshot = normalizeCartStockSnapshot(item.stockSnapshot);
  }

  return normalized;
};

const normalizeCart = (cart) => {
  const normalized = Array.isArray(cart)
    ? cart.map((item) => normalizeCartItem(item))
    : [];
  return reconcileCartNeedAllocations(normalized);
};

// Helper to get current user info for immediate UI feedback
const getCurrentUserInfo = () => {
  const userData = store.getters["auth/userData"];
  if (!userData) return null;
  return {
    id: userData.user?.id,
    name: userData.user?.full_name,
  };
};

// API helper for cart sync with debouncing
const syncCartToBackend = async (
  cart,
  supplierNotes,
  restaurantId,
  updatedItemIds = []
) => {
  try {
    if (!restaurantId) return;

    // Immediately update lastUpdatedBy with current user for instant UI feedback
    const currentUser = getCurrentUserInfo();
    if (currentUser) {
      store.commit("cart/setLastUpdatedBy", currentUser);

      // Also update lastModifiedBy for changed items
      if (updatedItemIds.length > 0) {
        store.commit("cart/updateItemsLastModifiedBy", {
          itemIds: updatedItemIds,
          userInfo: { ...currentUser, modifiedAt: new Date().toISOString() },
        });
      }
    }

    // Clear existing timer
    if (syncTimer) {
      clearTimeout(syncTimer);
    }

    // Set new timer for debouncing
    syncTimer = setTimeout(async () => {
      // Clean supplierNotes - remove null values and convert to proper object
      const cleanSupplierNotes = {};
      if (supplierNotes && typeof supplierNotes === "object") {
        Object.keys(supplierNotes).forEach((key) => {
          const value = supplierNotes[key];
          if (value !== null && value !== undefined && value !== "") {
            cleanSupplierNotes[key] = value;
          }
        });
      }

      // Read latest deviationReport from store at the time of sync
      const deviationReport = store.state.cart?.deviationReport || null;

      await axiosInstance.post(api.supplierOrders.cart.sync(restaurantId), {
        cart: normalizeCart(cart),
        supplierNotes: cleanSupplierNotes,
        deviationReport,
      });
    }, SYNC_DELAY);
  } catch (error) {
    console.error("Error syncing cart to backend:", error);
  }
};

// Load cart and notes from localStorage
const loadCartFromStorage = () => {
  try {
    const savedData = localStorage.getItem(CART_STORAGE_KEY);
    if (savedData) {
      const parsed = JSON.parse(savedData);
      // Support both old format (array) and new format (object with cart and notes)
      if (Array.isArray(parsed)) {
        return { cart: normalizeCart(parsed), supplierNotes: {}, deviationReport: null };
      }

      let supplierNotes = parsed.supplierNotes || {};

      // Convert array to object if needed (fix for old data)
      if (Array.isArray(supplierNotes)) {
        supplierNotes = {};
      }

      // Clean supplierNotes - remove null values
      const cleanSupplierNotes = {};
      if (supplierNotes && typeof supplierNotes === "object") {
        Object.keys(supplierNotes).forEach((key) => {
          const value = supplierNotes[key];
          if (value !== null && value !== undefined && value !== "") {
            cleanSupplierNotes[key] = value;
          }
        });
      }

      return {
        cart: normalizeCart(parsed.cart || []),
        supplierNotes: cleanSupplierNotes,
        deviationReport: parsed.deviationReport || null,
      };
    }
  } catch (error) {
    console.error("Error loading cart from localStorage:", error);
  }
  return { cart: [], supplierNotes: {}, deviationReport: null };
};

// Save cart and notes to localStorage
const saveCartToStorage = (cart, supplierNotes, deviationReport) => {
  try {
    localStorage.setItem(
      CART_STORAGE_KEY,
      JSON.stringify({
        cart: normalizeCart(cart),
        supplierNotes,
        deviationReport: deviationReport || null,
      })
    );
  } catch (error) {
    console.error("Error saving cart to localStorage:", error);
  }
};

const initialData = loadCartFromStorage();

const state = {
  cart: initialData.cart,
  supplierNotes: initialData.supplierNotes,
  deviationReport: initialData.deviationReport,
  currentRestaurantId: null,
  lastSyncedAt: null,
  lastSyncedRestaurantId: null,
  // Cart creator and last updater info (from backend)
  createdBy: null,
  lastUpdatedBy: null,
};

const mutations = {
  addToCart(state, item) {
    const existingItemIndex = state.cart.findIndex(
      (cartItem) => cartItem.id == item.id
    );
    const normalizedItem = normalizeCartItem(item);

    if (existingItemIndex != -1) {
      // If item already exists, increase quantity by the amount being added
      const quantity =
        (parseFloat(state.cart[existingItemIndex].quantity) || 0) +
        (parseFloat(normalizedItem.quantity) || 0);
      state.cart.splice(existingItemIndex, 1, normalizeCartItem({
        ...state.cart[existingItemIndex],
        ...normalizedItem,
        stockSnapshot: mergeCartStockSnapshots(
          state.cart[existingItemIndex].stockSnapshot,
          normalizedItem.stockSnapshot
        ),
        quantity,
      }));
    } else {
      // Add new item to cart
      state.cart.push(normalizedItem);
    }

    state.cart = reconcileCartNeedAllocations(state.cart);

    // Save to localStorage
    saveCartToStorage(state.cart, state.supplierNotes, state.deviationReport);

    // Sync to backend (backend will add lastModifiedBy)
    syncCartToBackend(
      state.cart,
      state.supplierNotes,
      state.currentRestaurantId,
      [item.id] // Track which item was modified
    );
  },

  bulkAddToCart(state, items) {
    const normalizedItems = Array.isArray(items)
      ? items.map((item) => normalizeCartItem(item))
      : [];

    normalizedItems.forEach((item) => {
      const existingItemIndex = state.cart.findIndex(
        (cartItem) => cartItem.id == item.id
      );

      if (existingItemIndex !== -1) {
        state.cart.splice(existingItemIndex, 1, normalizeCartItem({
          ...state.cart[existingItemIndex],
          ...item,
          stockSnapshot: mergeCartStockSnapshots(
            state.cart[existingItemIndex].stockSnapshot,
            item.stockSnapshot
          ),
          quantity:
            (parseFloat(state.cart[existingItemIndex].quantity) || 0) +
            (parseFloat(item.quantity) || 0),
        }));
      } else {
        state.cart.push(item);
      }
    });

    state.cart = reconcileCartNeedAllocations(state.cart);

    saveCartToStorage(state.cart, state.supplierNotes, state.deviationReport);
    syncCartToBackend(
      state.cart,
      state.supplierNotes,
      state.currentRestaurantId,
      normalizedItems.map((item) => item.id)
    );
  },

  updateCartItemQuantity(state, { itemId, quantity }) {
    const itemIndex = state.cart.findIndex((item) => item.id == itemId);
    if (itemIndex != -1) {
      if (quantity <= 0) {
        state.cart.splice(itemIndex, 1);
      } else {
        state.cart[itemIndex].quantity = quantity;
      }
    }

    state.cart = reconcileCartNeedAllocations(state.cart);

    // Save to localStorage
    saveCartToStorage(state.cart, state.supplierNotes, state.deviationReport);

    // Sync to backend (backend will add lastModifiedBy)
    syncCartToBackend(
      state.cart,
      state.supplierNotes,
      state.currentRestaurantId,
      [itemId] // Track which item was modified
    );
  },

  removeFromCart(state, itemId) {
    const itemIndex = state.cart.findIndex((item) => item.id == itemId);
    if (itemIndex != -1) {
      state.cart.splice(itemIndex, 1);
    }

    state.cart = reconcileCartNeedAllocations(state.cart);

    // If cart is empty, reset deviation report
    if (state.cart.length === 0) {
      state.deviationReport = null;
    }

    // Save to localStorage
    saveCartToStorage(state.cart, state.supplierNotes, state.deviationReport);

    // Sync to backend (item removed, no need to track lastModifiedBy)
    syncCartToBackend(
      state.cart,
      state.supplierNotes,
      state.currentRestaurantId
    );
  },

  clearCart(state) {
    state.cart = [];
    state.supplierNotes = {};
    state.deviationReport = null;
    state.lastSyncedAt = Date.now();
    state.lastSyncedRestaurantId = state.currentRestaurantId;
    state.createdBy = null;
    state.lastUpdatedBy = null;

    // Save to localStorage
    saveCartToStorage(state.cart, state.supplierNotes, state.deviationReport);

    // Sync to backend
    syncCartToBackend(
      state.cart,
      state.supplierNotes,
      state.currentRestaurantId
    );
  },

  clearSupplierCart(state, supplierId) {
    state.cart = state.cart.filter((item) => item.supplierId != supplierId);
    state.cart = reconcileCartNeedAllocations(state.cart);

    // Clear supplier notes
    delete state.supplierNotes[supplierId];

    // If cart is empty, reset deviation report
    if (state.cart.length === 0) {
      state.deviationReport = null;
    }

    // Save to localStorage
    saveCartToStorage(state.cart, state.supplierNotes, state.deviationReport);

    // Sync to backend
    syncCartToBackend(
      state.cart,
      state.supplierNotes,
      state.currentRestaurantId
    );
  },

  setSupplierNotes(state, { supplierId, notes }) {
    // Only save non-empty notes
    if (notes && notes.trim()) {
      state.supplierNotes[supplierId] = notes;
    } else {
      // Remove empty notes
      delete state.supplierNotes[supplierId];
    }

    // Save to localStorage immediately
    saveCartToStorage(state.cart, state.supplierNotes, state.deviationReport);

    // Sync to backend with debouncing
    syncCartToBackend(
      state.cart,
      state.supplierNotes,
      state.currentRestaurantId
    );
  },

  clearSupplierNotes(state, supplierId) {
    delete state.supplierNotes[supplierId];

    // Save to localStorage
    saveCartToStorage(state.cart, state.supplierNotes, state.deviationReport);

    // Sync to backend
    syncCartToBackend(
      state.cart,
      state.supplierNotes,
      state.currentRestaurantId
    );
  },

  setCurrentRestaurantId(state, restaurantId) {
    state.currentRestaurantId = restaurantId;
  },

  setCartMetadata(state, { createdBy, lastUpdatedBy }) {
    state.createdBy = createdBy;
    state.lastUpdatedBy = lastUpdatedBy;
  },

  setLastUpdatedBy(state, userInfo) {
    state.lastUpdatedBy = userInfo;
  },

  updateItemsLastModifiedBy(state, { itemIds, userInfo }) {
    state.cart.forEach((item) => {
      if (itemIds.includes(item.id)) {
        item.lastModifiedBy = userInfo;
      }
    });
  },

  setItemDeviationDecision(state, { itemId, decision }) {
    const itemIndex = state.cart.findIndex((item) => item.id == itemId);
    if (itemIndex === -1) {
      return;
    }

    if (decision) {
      state.cart[itemIndex].stockDeviationDecision = decision;
    } else {
      delete state.cart[itemIndex].stockDeviationDecision;
    }

    // Save and sync so decision is persistent across navigation/reload
    saveCartToStorage(state.cart, state.supplierNotes, state.deviationReport);
    syncCartToBackend(
      state.cart,
      state.supplierNotes,
      state.currentRestaurantId,
      [itemId]
    );
  },

  reconcileNeedContexts(state, contextsByProductId) {
    state.cart = reconcileCartNeedAllocations(
      state.cart,
      contextsByProductId || {}
    );

    saveCartToStorage(state.cart, state.supplierNotes, state.deviationReport);
    syncCartToBackend(
      state.cart,
      state.supplierNotes,
      state.currentRestaurantId
    );
  },

  setDeviationReport(state, reportData) {
    state.deviationReport = reportData || null;

    // Save to localStorage
    saveCartToStorage(state.cart, state.supplierNotes, state.deviationReport);

    // Sync to backend with debouncing (deviationReport is read from state inside syncCartToBackend)
    syncCartToBackend(
      state.cart,
      state.supplierNotes,
      state.currentRestaurantId
    );
  },
};

const actions = {
  addToCart({ commit }, item) {
    commit("addToCart", item);
  },
  bulkAddToCart({ commit }, items) {
    commit("bulkAddToCart", items);
  },

  reconcileNeedContexts({ commit }, contextsByProductId) {
    commit("reconcileNeedContexts", contextsByProductId);
  },

  updateQuantity({ commit }, { itemId, quantity }) {
    commit("updateCartItemQuantity", { itemId, quantity });
  },

  removeItem({ commit }, itemId) {
    commit("removeFromCart", itemId);
  },

  clearCart({ commit }) {
    commit("clearCart");
  },

  clearSupplierCart({ commit }, supplierId) {
    commit("clearSupplierCart", supplierId);
  },

  setItemDeviationDecision({ commit }, payload) {
    commit("setItemDeviationDecision", payload);
  },

  setDeviationReport({ commit }, reportData) {
    commit("setDeviationReport", reportData);
  },

  async loadCartFromBackend({ commit, state }, restaurantId) {
    try {
      const response = await axiosInstance.get(
        api.supplierOrders.cart.load(restaurantId)
      );

      if (response.data.success) {
        const { cart, supplierNotes, deviationReport, createdBy, lastUpdatedBy } =
          response.data.data;

        // Clean supplierNotes - convert array to object if needed
        let cleanSupplierNotes = supplierNotes || {};
        if (Array.isArray(cleanSupplierNotes)) {
          cleanSupplierNotes = {};
        }

        // Remove null values
        const filteredNotes = {};
        if (cleanSupplierNotes && typeof cleanSupplierNotes === "object") {
          Object.keys(cleanSupplierNotes).forEach((key) => {
            const value = cleanSupplierNotes[key];
            if (value !== null && value !== undefined && value !== "") {
              filteredNotes[key] = value;
            }
          });
        }

        // Update state
      state.cart = normalizeCart(cart || []);
        state.supplierNotes = filteredNotes;
        state.deviationReport = deviationReport || null;
        state.lastSyncedRestaurantId = restaurantId;
        state.lastSyncedAt = Date.now();

        // Save cart metadata (creator and last updater)
        commit("setCartMetadata", { createdBy, lastUpdatedBy });

        // Save to localStorage
        saveCartToStorage(state.cart, filteredNotes, state.deviationReport);

        // Set current restaurant ID (this will also subscribe to WebSocket channel)
        store.dispatch("cart/setCurrentRestaurantId", restaurantId);
      }
    } catch (error) {
      console.error("Error loading cart from backend:", error);
    }
  },

  // Echo öncesi kullanılan fonksiyon
  // setCurrentRestaurantId({ commit }, restaurantId) {
  //   commit("setCurrentRestaurantId", restaurantId);
  // },

  setCurrentRestaurantId({ commit, state }, restaurantId) {
    const previousRestaurantId = state.currentRestaurantId;
    const Echo = getEcho();

    // If restaurantId hasn't changed and channel already exists, don't resubscribe
    if (previousRestaurantId === restaurantId && window.cartChannel) {
      return; // Already subscribed to this channel
    }

    // Unsubscribe from previous channel if exists
    if (previousRestaurantId && Echo && window.cartChannel) {
      window.cartChannel.stopListening(".cart.updated");
      Echo.leave(`cart.firm.${previousRestaurantId}`);
      window.cartChannel = null;
    }

    commit("setCurrentRestaurantId", restaurantId);

    // Subscribe to new channel if restaurantId is provided and different from previous
    if (restaurantId && Echo && restaurantId !== previousRestaurantId) {
      const channelName = `cart.firm.${restaurantId}`;
      const channel = Echo.private(channelName);

      channel.listen(".cart.updated", (event) => {
        // Don't reload if this update came from current user's action
        // (to avoid redundant API calls)
        const currentUser = store.getters["auth/userData"];
        const eventUserId = event.lastUpdatedBy?.id;
        const currentUserId = currentUser?.user?.id;

        // Only reload if the event came from another user
        if (eventUserId && currentUserId && eventUserId !== currentUserId) {
          // Fetch cart from API when notification received
          store.dispatch("cart/loadCartFromBackend", restaurantId);
        }
      });

      channel.error((error) => {
        console.error("[Cart Store] Channel subscription error:", error);
      });

      window.cartChannel = channel;
    }
  },
};

const getters = {
  cartItems: (state) => state.cart,

  // cartItemCount: (state) => state.cart.reduce((total, item) => total + item.quantity, 0),
  cartItemCount: (state) => state.cart.length,

  cartCreatedBy: (state) => state.createdBy,

  cartLastUpdatedBy: (state) => state.lastUpdatedBy,

  cartDeviationReport: (state) => state.deviationReport,

  cartTotal: (state) => {
    const total = state.cart.reduce((total, item) => {
      if (item.price && item.price.amount) {
        return total + item.price.amount * item.quantity;
      }
      return total;
    }, 0);

    // Get currency from first item that has a price
    const currency =
      state.cart.find((item) => item.price?.currency)?.price?.currency || "EUR";

    return {
      amount: total + state.cart.reduce(
        (depositTotal, item) =>
          depositTotal + (parseFloat(item.depositEstimate?.amount) || 0) * (parseFloat(item.quantity) || 0),
        0
      ),
      currency: currency,
    };
  },

  cartMerchandiseTotal: (state) => ({
    amount: state.cart.reduce(
      (total, item) => total + (parseFloat(item.price?.amount) || 0) * (parseFloat(item.quantity) || 0),
      0
    ),
    currency: state.cart.find(item => item.price?.currency)?.price?.currency || 'EUR',
  }),

  cartDepositTotal: (state) => ({
    amount: state.cart.reduce(
      (total, item) => total + (parseFloat(item.depositEstimate?.amount) || 0) * (parseFloat(item.quantity) || 0),
      0
    ),
    currency:
      state.cart.find(item => item.depositEstimate?.currency)?.depositEstimate?.currency
      || state.cart.find(item => item.price?.currency)?.price?.currency
      || 'EUR',
  }),

  cartItemsBySupplier: (state) => {
    const groupedItems = {};
    state.cart.forEach((item) => {
      if (!groupedItems[item.supplierId]) {
        groupedItems[item.supplierId] = {
          supplier: item.supplier,
          items: [],
          total: {
            amount: 0,
            currency: item.price?.currency || "EUR",
          },
        };
      }
      groupedItems[item.supplierId].items.push(item);
      if (item.price && item.price.amount) {
        groupedItems[item.supplierId].total.amount +=
          item.price.amount * item.quantity;
        groupedItems[item.supplierId].total.currency =
          item.price?.currency || "EUR";
      }
    });
    return groupedItems;
  },

  hasCartItems: (state) => state.cart.length > 0,

  supplierCount: (state, getters) => {
    return Object.keys(getters.cartItemsBySupplier).length;
  },

  getSupplierNotes: (state) => (supplierId) => {
    return state.supplierNotes[supplierId] || "";
  },

  cartItemsBySupplierAndDepartment: (state) => {
    const groupedItems = {};
    state.cart.forEach((item) => {
      const supplierId = item.supplierId;
      const departmentId = item.product?.department_id || null;

      // Initialize supplier group if not exists
      if (!groupedItems[supplierId]) {
        groupedItems[supplierId] = {
          supplier: item.supplier,
          departments: {},
          total: {
            amount: 0,
            currency: item.price?.currency || "EUR",
          },
        };
      }

      // Initialize department group if not exists
      const deptKey = departmentId || 'uncategorized';
      if (!groupedItems[supplierId].departments[deptKey]) {
        groupedItems[supplierId].departments[deptKey] = {
          departmentId: departmentId,
          items: [],
          total: {
            amount: 0,
            currency: item.price?.currency || "EUR",
          },
        };
      }

      // Add item to department group
      groupedItems[supplierId].departments[deptKey].items.push(item);

      // Update department total
      if (item.price && item.price.amount) {
        groupedItems[supplierId].departments[deptKey].total.amount +=
          item.price.amount * item.quantity;
        groupedItems[supplierId].departments[deptKey].total.currency =
          item.price?.currency || "EUR";
      }

      // Update supplier total
      if (item.price && item.price.amount) {
        groupedItems[supplierId].total.amount +=
          item.price.amount * item.quantity;
        groupedItems[supplierId].total.currency =
          item.price?.currency || "EUR";
      }
    });
    return groupedItems;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
